import type { VehicleCoverages, CarriersVehicle } from 'api/quote-marketplace';
import { formatCurrency, formatCoverageLabel } from '../constants';

type GetLabelFn = (value: string, field: any) => string;

const getEndorsementDisplay = (endorsement: unknown): string | undefined => {
  if (!endorsement) return undefined;
  if (typeof endorsement === 'string') {
    const num = parseFloat(endorsement);
    if (!isNaN(num)) return formatCurrency(endorsement);
    return endorsement;
  }
  if (typeof endorsement === 'object' && endorsement !== null) {
    const obj = endorsement as Record<string, unknown>;

    // Check for limit values (format as currency)
    if (obj.limit_cad != null) return formatCurrency(String(obj.limit_cad));
    if (obj.limit_per_day_cad != null) {
      return `${formatCurrency(String(obj.limit_per_day_cad))}/day`;
    }

    // Check for any other _cad properties
    for (const key of Object.keys(obj)) {
      if (key.endsWith('_cad') && obj[key] != null) {
        return formatCurrency(String(obj[key]));
      }
    }

    // Check for indemnity period (for depreciation endorsements)
    if (obj.indemnity_period_months != null) {
      return `${obj.indemnity_period_months} months`;
    }

    // Fall back to endorsement_coverage or coverage
    if (
      typeof obj.endorsement_coverage === 'string' &&
      obj.endorsement_coverage
    ) {
      return obj.endorsement_coverage;
    }
    if (typeof obj.coverage === 'string' && obj.coverage) {
      return obj.coverage;
    }
  }
  return 'Included';
};

const formatCoverageOptions = (
  options: string[] | undefined,
  getLabel: GetLabelFn,
  field: string,
): string => {
  if (!options || options.length === 0) return '';
  return options.map((code) => getLabel(code, field) || code).join(', ');
};

const formatOptionalLimit = (limit: number | null | undefined): string => {
  if (limit === null || limit === undefined) return '';
  return formatCurrency(String(limit));
};

type RatedVehicleCoverages = any;

export type AutoSectionKey =
  | 'physicalDamage'
  | 'endorsements'
  | 'discounts'
  | 'standardAB'
  | 'optionalAB';

export interface AutoCoverageRowConfig {
  label: string;
  coverageKey?: string;
  getRequestedValue: (
    coverages: VehicleCoverages,
    vehicle: CarriersVehicle,
    getLabel: GetLabelFn,
  ) => string;
  getRatedValue?: (
    coverages: RatedVehicleCoverages,
    getLabel?: GetLabelFn,
  ) => string | undefined;
  getRawRequestedValue?: (
    coverages: VehicleCoverages,
    vehicle: CarriersVehicle,
    getLabel?: GetLabelFn,
  ) => unknown;
  getRawRatedValue?: (coverages: RatedVehicleCoverages) => unknown;
  codeField?: string;
  showInCarrierCells?: (
    coverages: VehicleCoverages,
    vehicle: CarriersVehicle,
  ) => boolean;
  isRequested?: (
    coverages: VehicleCoverages,
    vehicle: CarriersVehicle,
  ) => boolean;
}

export interface AutoCoverageSectionConfig {
  key: AutoSectionKey;
  label: string;
  rows: AutoCoverageRowConfig[];
}

export const LIABILITY_ROW: AutoCoverageRowConfig = {
  label: 'Liability',
  getRequestedValue: (coverages, _, getLabel) =>
    formatCoverageLabel(
      coverages.liability_limit_cad,
      getLabel,
      'liability_limit_cad',
    ),
  getRatedValue: (c) => c.liability?.limit_cad,
  getRawRequestedValue: (coverages) => coverages.liability_limit_cad,
  getRawRatedValue: (c) => c.liability?.limit_cad,
  codeField: 'liability_limit_cad',
  isRequested: () => true,
};

export const PHYSICAL_DAMAGE_ROWS: AutoCoverageRowConfig[] = [
  {
    label: 'Collision',
    getRequestedValue: (coverages, _, getLabel) =>
      coverages.collision_enabled
        ? formatCoverageLabel(
            coverages.collision_deductible_cad,
            getLabel,
            'collision_deductible_cad',
            'Base',
          )
        : 'Not selected',
    getRatedValue: (c) =>
      c.collision_or_upset?.deductible_other_cad
        ? formatCurrency(String(c.collision_or_upset.deductible_other_cad))
        : (c.collision_or_upset?.deductible_cad ??
          (c.collision_or_upset?.coverage === 'Included' ? 'Base' : undefined)),
    getRawRequestedValue: (coverages) =>
      coverages.collision_deductible_cad ??
      (coverages.collision_enabled ? 'BASE' : undefined),
    getRawRatedValue: (c) =>
      c.collision_or_upset?.deductible_other_cad ??
      c.collision_or_upset?.deductible_cad ??
      (c.collision_or_upset?.coverage === 'Included' ? 'BASE' : undefined),
    codeField: 'collision_deductible_cad',
    showInCarrierCells: (coverages) => coverages.collision_enabled,
    isRequested: (coverages) => coverages.collision_enabled,
  },
  {
    label: 'Comprehensive',
    getRequestedValue: (coverages, _, getLabel) =>
      coverages.comprehensive_enabled
        ? formatCoverageLabel(
            coverages.comprehensive_deductible_cad,
            getLabel,
            'comprehensive_deductible_cad',
            'Base',
          )
        : 'Not selected',
    getRatedValue: (c) =>
      c.comprehensive?.deductible_other_cad
        ? formatCurrency(String(c.comprehensive.deductible_other_cad))
        : (c.comprehensive?.deductible_cad ??
          (c.comprehensive?.coverage === 'Included' ? 'Base' : undefined)),
    getRawRequestedValue: (coverages) =>
      coverages.comprehensive_deductible_cad ??
      (coverages.comprehensive_enabled ? 'BASE' : undefined),
    getRawRatedValue: (c) =>
      c.comprehensive?.deductible_other_cad ??
      c.comprehensive?.deductible_cad ??
      (c.comprehensive?.coverage === 'Included' ? 'BASE' : undefined),
    codeField: 'comprehensive_deductible_cad',
    showInCarrierCells: (coverages) => coverages.comprehensive_enabled,
    isRequested: (coverages) => coverages.comprehensive_enabled,
  },
  {
    label: 'All Perils',
    getRequestedValue: (coverages, _, getLabel) =>
      coverages.all_perils_enabled
        ? formatCoverageLabel(
            coverages.all_perils_deductible_cad,
            getLabel,
            'all_perils_deductible_cad',
            'Base',
          )
        : 'Not selected',
    getRatedValue: (c) =>
      c.all_perils?.deductible_other_cad
        ? formatCurrency(String(c.all_perils.deductible_other_cad))
        : (c.all_perils?.deductible_cad ??
          (c.all_perils?.coverage === 'Included' ? 'Base' : undefined)),
    getRawRequestedValue: (coverages) =>
      coverages.all_perils_deductible_cad ??
      (coverages.all_perils_enabled ? 'BASE' : undefined),
    getRawRatedValue: (c) =>
      c.all_perils?.deductible_other_cad ??
      c.all_perils?.deductible_cad ??
      (c.all_perils?.coverage === 'Included' ? 'BASE' : undefined),
    codeField: 'all_perils_deductible_cad',
    showInCarrierCells: (coverages) => coverages.all_perils_enabled,
    isRequested: (coverages) => coverages.all_perils_enabled,
  },
  {
    label: 'DCPD',
    coverageKey: 'dcpd_deductible',
    getRequestedValue: (coverages, _, getLabel) =>
      coverages.dcpd_deductible_cad
        ? formatCoverageLabel(
            coverages.dcpd_deductible_cad,
            getLabel,
            'dcpd_deductible_cad',
          )
        : 'Not selected',
    getRatedValue: (c) =>
      c.dcpd?.deductible_other_cad
        ? formatCurrency(String(c.dcpd.deductible_other_cad))
        : c.dcpd?.deductible_cad,
    getRawRequestedValue: (coverages) => coverages.dcpd_deductible_cad,
    getRawRatedValue: (c) =>
      c.dcpd?.deductible_other_cad ?? c.dcpd?.deductible_cad,
    codeField: 'dcpd_deductible_cad',
    showInCarrierCells: (coverages) => !!coverages.dcpd_deductible_cad,
    isRequested: (coverages) => !!coverages.dcpd_deductible_cad,
  },
];

export const ENDORSEMENT_ROWS: AutoCoverageRowConfig[] = [
  {
    label: 'Accident Waiver',
    coverageKey: 'accident_waiver',
    getRequestedValue: (coverages) =>
      coverages.accident_waiver ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf39_accident_waiver ||
          c.sef39_atfault_accident_waiver ||
          c.nbef39_accident_rating_waiver ||
          c.sef39a_atfault_accident_waiver,
      ),
    getRawRequestedValue: (coverages) => coverages.accident_waiver,
    getRawRatedValue: (c) =>
      !!(
        c.opcf39_accident_waiver ||
        c.sef39_atfault_accident_waiver ||
        c.nbef39_accident_rating_waiver ||
        c.sef39a_atfault_accident_waiver
      ),
    isRequested: (coverages) => coverages.accident_waiver,
  },
  {
    label: '#2 - Drive Other Autos',
    coverageKey: 'opcf_2',
    getRequestedValue: (coverages) => (coverages.opcf_2_enabled ? 'Yes' : 'No'),
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf2_providing_coverage_when_named_persons_drive_other_automobiles ||
          c.sef2_designated_operators_drive_other_automobiles ||
          c.nbef2_drive_other_automobiles_named_persons,
      ),
    getRawRequestedValue: (coverages) => coverages.opcf_2_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.opcf2_providing_coverage_when_named_persons_drive_other_automobiles ||
        c.sef2_designated_operators_drive_other_automobiles ||
        c.nbef2_drive_other_automobiles_named_persons
      ),
    isRequested: (coverages) => coverages.opcf_2_enabled,
  },
  {
    label: '#20 - Loss of Use',
    coverageKey: 'opcf_20',
    getRequestedValue: (coverages) =>
      coverages.opcf_20_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf20_coverage_for_transportation_replacement ||
          c.sef20_cae20_loss_of_use ||
          c.nbef20_loss_of_use,
      ),
    getRawRequestedValue: (coverages) => coverages.opcf_20_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.opcf20_coverage_for_transportation_replacement ||
        c.sef20_cae20_loss_of_use ||
        c.nbef20_loss_of_use
      ),
    isRequested: (coverages) => coverages.opcf_20_enabled,
  },
  {
    label: '#20 - Disappearing Deductible',
    coverageKey: 'opcf_20_disappearing',
    getRequestedValue: (coverages) =>
      coverages.opcf_20_disappearing_deductible ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.sef26_disappearing_deductible_endorsement_all_perils_or_collisions_or_upset ||
          c.nbef26_disappearing_deductible ||
          c.cae3_disappearing_deductible,
      ),
    getRawRequestedValue: (coverages) =>
      coverages.opcf_20_disappearing_deductible,
    getRawRatedValue: (c) =>
      !!(
        c.sef26_disappearing_deductible_endorsement_all_perils_or_collisions_or_upset ||
        c.nbef26_disappearing_deductible ||
        c.cae3_disappearing_deductible
      ),
    isRequested: (coverages) => coverages.opcf_20_disappearing_deductible,
  },
  {
    label: '#27 - Legal Liability',
    coverageKey: 'opcf_27',
    getRequestedValue: (coverages) =>
      coverages.opcf_27_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf27_liability_for_damage_to_nonowned_automobile ||
          c.sef27_legal_liability_for_damage_to_nonowned_automobiles ||
          c.nbef27_legal_liability_for_damage_to_nonowned_automobiles,
      ),
    getRawRequestedValue: (coverages) => coverages.opcf_27_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.opcf27_liability_for_damage_to_nonowned_automobile ||
        c.sef27_legal_liability_for_damage_to_nonowned_automobiles ||
        c.nbef27_legal_liability_for_damage_to_nonowned_automobiles
      ),
    isRequested: (coverages) => coverages.opcf_27_enabled,
  },
  {
    label: '#43 - Removing Depreciation',
    coverageKey: 'opcf_43',
    getRequestedValue: (coverages) =>
      coverages.opcf_43_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf43_removing_depreciation_deduction ||
          c.opcf43a_removing_depreciation_deduction ||
          c.sef43r_limited_waiver_of_depreciation ||
          c.sef43rl_specified_lessee_limited_waiver_of_depreciation ||
          c.nbef43r_limited_waiver_of_depreciation ||
          c.nbef43rl_specified_lessee_limited_waiver_of_depreciation,
      ),
    getRawRequestedValue: (coverages) => coverages.opcf_43_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.opcf43_removing_depreciation_deduction ||
        c.opcf43a_removing_depreciation_deduction ||
        c.sef43r_limited_waiver_of_depreciation ||
        c.sef43rl_specified_lessee_limited_waiver_of_depreciation ||
        c.nbef43r_limited_waiver_of_depreciation ||
        c.nbef43rl_specified_lessee_limited_waiver_of_depreciation
      ),
    isRequested: (coverages) => coverages.opcf_43_enabled,
  },
  {
    label: '#23 - Lienholder',
    getRequestedValue: (coverages) =>
      coverages.lienholder_endorsement_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf23a_lienholder_for_use_with_oap_no_1_owners_form ||
          c.sef23a_lienholder_mortgagee_or_assignee_endorsement ||
          c.nbef23a_mortgage_endorsement,
      ),
    getRawRequestedValue: (coverages) =>
      coverages.lienholder_endorsement_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.opcf23a_lienholder_for_use_with_oap_no_1_owners_form ||
        c.sef23a_lienholder_mortgagee_or_assignee_endorsement ||
        c.nbef23a_mortgage_endorsement
      ),
    isRequested: (coverages) => coverages.lienholder_endorsement_enabled,
  },
  {
    label: '#5 - Permission to Rent/Lease',
    getRequestedValue: (coverages) =>
      coverages.lessor_endorsement_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf5_permission_to_rent_or_lease_automobiles_and_extending_coverage_to_the_specific_lessees ||
          c.sef5_permission_to_rent_or_lease ||
          c.nbef5_permission_to_rent_or_lease,
      ),
    getRawRequestedValue: (coverages) => coverages.lessor_endorsement_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.opcf5_permission_to_rent_or_lease_automobiles_and_extending_coverage_to_the_specific_lessees ||
        c.sef5_permission_to_rent_or_lease ||
        c.nbef5_permission_to_rent_or_lease
      ),
    isRequested: (coverages) => coverages.lessor_endorsement_enabled,
  },
  {
    label: '#44 - Family Protection',
    coverageKey: 'opcf_44',
    getRequestedValue: (coverages) =>
      coverages.opcf_44_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf44r_family_protection_coverage ||
          c.sef44_family_protection ||
          c.nbef44_family_protection,
      ),
    getRawRequestedValue: (coverages) => coverages.opcf_44_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.opcf44r_family_protection_coverage ||
        c.sef44_family_protection ||
        c.nbef44_family_protection
      ),
    isRequested: (coverages) => coverages.opcf_44_enabled,
  },
  {
    label: '#48 - Offset Tort Deductibles',
    coverageKey: 'opcf_48',
    getRequestedValue: (coverages) =>
      coverages.opcf_48_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(c.opcf48_added_coverage_to_offset_tort_deductibles),
    getRawRequestedValue: (coverages) => coverages.opcf_48_enabled,
    getRawRatedValue: (c) =>
      !!c.opcf48_added_coverage_to_offset_tort_deductibles,
    isRequested: (coverages) => coverages.opcf_48_enabled,
  },
  {
    label: '#49 - Agreement Not to Recover',
    coverageKey: 'opcf_49',
    getRequestedValue: (coverages) =>
      coverages.opcf_49_enabled ? 'Yes' : 'No',
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf49_agreement_not_to_recover_for_loss_or_damage_from_an_automobile_collision,
      ),
    getRawRequestedValue: (coverages) => coverages.opcf_49_enabled,
    getRawRatedValue: (c) =>
      !!c.opcf49_agreement_not_to_recover_for_loss_or_damage_from_an_automobile_collision,
    isRequested: (coverages) => coverages.opcf_49_enabled,
  },
  {
    label: '#28A - Excluded Driver',
    coverageKey: 'opcf_28a',
    getRequestedValue: (_, vehicle) => {
      const hasExcludedDrivers = (vehicle as any).has_excluded_drivers;
      return hasExcludedDrivers ? 'Yes' : 'No';
    },
    getRatedValue: (c) =>
      getEndorsementDisplay(
        c.opcf28a_excluded_driver ||
          c.sef28a_excluded_driver ||
          c.nbef28a_excluded_driver,
      ),
    getRawRequestedValue: (_, vehicle) =>
      (vehicle as any).has_excluded_drivers ?? false,
    getRawRatedValue: (c) =>
      !!(
        c.opcf28a_excluded_driver ||
        c.sef28a_excluded_driver ||
        c.nbef28a_excluded_driver
      ),
    isRequested: (_, vehicle) => (vehicle as any).has_excluded_drivers ?? false,
  },
  {
    label: 'SEF33 - Pedestrian Coverage',
    coverageKey: 'sef33_pedestrian_coverage',
    getRequestedValue: (coverages) => (coverages.sef33_enabled ? 'Yes' : 'No'),
    getRatedValue: (c) =>
      getEndorsementDisplay(c.sef33_accident_benefits_pedestrian_coverage),
    getRawRequestedValue: (coverages) => coverages.sef33_enabled,
    getRawRatedValue: (c) => !!c.sef33_accident_benefits_pedestrian_coverage,
    isRequested: (coverages) => coverages.sef33_enabled,
  },
  {
    label: 'SEF48 - Supplementary Benefits',
    coverageKey: 'sef48_supplementary_benefits',
    getRequestedValue: (coverages) => (coverages.sef48_enabled ? 'Yes' : 'No'),
    getRatedValue: (c) =>
      getEndorsementDisplay(c.sef48_optional_supplementary_benefits),
    getRawRequestedValue: (coverages) => coverages.sef48_enabled,
    getRawRatedValue: (c) => !!c.sef48_optional_supplementary_benefits,
    isRequested: (coverages) => coverages.sef48_enabled,
  },
];

export const DISCOUNT_ROWS: AutoCoverageRowConfig[] = [
  {
    label: 'Telematics',
    getRequestedValue: (_, vehicle) =>
      vehicle.telematics_enabled ? 'Yes' : 'No',
    getRatedValue: (c) => {
      const discounts = c.discounts || [];
      const match = discounts.find((d: string) =>
        /telematics|usage.?based|ubi/i.test(d),
      );
      return match || undefined;
    },
    getRawRequestedValue: (_, vehicle) => vehicle.telematics_enabled,
    getRawRatedValue: (c) => {
      const discounts = c.discounts || [];
      return discounts.some((d: string) =>
        /telematics|usage.?based|ubi/i.test(d),
      );
    },
    isRequested: (_, vehicle) => vehicle.telematics_enabled,
  },
  {
    label: 'Winter Tires',
    getRequestedValue: (_, vehicle) => (vehicle.winter_tires ? 'Yes' : 'No'),
    getRatedValue: (c) => {
      const discounts = c.discounts || [];
      const match = discounts.find((d: string) =>
        /winter.?tire|snow.?tire/i.test(d),
      );
      return match || undefined;
    },
    getRawRequestedValue: (_, vehicle) => vehicle.winter_tires,
    getRawRatedValue: (c) => {
      const discounts = c.discounts || [];
      return discounts.some((d: string) => /winter.?tire|snow.?tire/i.test(d));
    },
    isRequested: (_, vehicle) => vehicle.winter_tires,
  },
  {
    label: 'Multi-Vehicle',
    getRequestedValue: (_, vehicle) =>
      vehicle.single_vehicle_mvd ? 'Yes' : 'No',
    getRatedValue: (c) => {
      const discounts = c.discounts || [];
      const match = discounts.find((d: string) => /multi.?vehicle/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (_, vehicle) => vehicle.single_vehicle_mvd,
    getRawRatedValue: (c) => {
      const discounts = c.discounts || [];
      return discounts.some((d: string) => /multi.?vehicle/i.test(d));
    },
    isRequested: (_, vehicle) => vehicle.single_vehicle_mvd,
  },
  {
    label: 'Combined Policy',
    getRequestedValue: (_, vehicle) =>
      vehicle.policy_discount === 'combined' ? 'Yes' : 'No',
    getRatedValue: (c) => {
      const discounts = c.discounts || [];
      const match = discounts.find((d: string) => /combined.?policy/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (_, vehicle) =>
      vehicle.policy_discount === 'combined',
    getRawRatedValue: (c) => {
      const discounts = c.discounts || [];
      return discounts.some((d: string) => /combined.?policy/i.test(d));
    },
    isRequested: (_, vehicle) => vehicle.policy_discount === 'combined',
  },
  {
    label: 'Multi-Line',
    getRequestedValue: (_, vehicle) =>
      vehicle.policy_discount === 'combined' ||
      vehicle.policy_discount === 'multi_line'
        ? 'Yes'
        : 'No',
    getRatedValue: (c) => {
      const discounts = c.discounts || [];
      const match = discounts.find((d: string) => /multi.?line/i.test(d));
      return match || undefined;
    },
    getRawRequestedValue: (_, vehicle) =>
      vehicle.policy_discount === 'combined' ||
      vehicle.policy_discount === 'multi_line',
    getRawRatedValue: (c) => {
      const discounts = c.discounts || [];
      return discounts.some((d: string) => /multi.?line/i.test(d));
    },
    isRequested: (_, vehicle) =>
      vehicle.policy_discount === 'combined' ||
      vehicle.policy_discount === 'multi_line',
  },
];

export const STANDARD_AB_ROWS: AutoCoverageRowConfig[] = [
  {
    label: 'Optional Increased Limits',
    getRequestedValue: () => 'Included',
    getRatedValue: (c) => c.accident_benefits?.coverage,
    getRawRequestedValue: () => true,
    getRawRatedValue: (c) => !!c.accident_benefits?.coverage,
    isRequested: () => true,
  },
];

const formatAccidentBenefitLabel = (
  code: string | undefined,
  getLabel: GetLabelFn | undefined,
  field: string,
  suffix?: string,
): string | undefined => {
  if (!code || !getLabel) return undefined;
  const label = getLabel(code, field);
  if (!label || label === code) return undefined;
  const formatted = label.replace(/^(\d)/, '$$$1');
  return suffix ? `${formatted}${suffix}` : formatted;
};

export const OPTIONAL_AB_ROWS: AutoCoverageRowConfig[] = [
  {
    label: 'Income Replacement',
    getRequestedValue: (coverages, _, getLabel) => {
      if (!coverages.accident_benefits?.income_replacement_enabled)
        return 'Declined';
      return formatCoverageLabel(
        coverages.accident_benefits?.income_replacement_limit_per_week_cad,
        getLabel,
        'income_replacement_limit_per_week_cad',
        'Base',
        '/wk',
      );
    },
    getRatedValue: (c, getLabel) =>
      formatAccidentBenefitLabel(
        c.increased_accident_benefits_income_replacement?.limit_per_week_cad,
        getLabel,
        'income_replacement_limit_per_week_cad',
        '/wk',
      ),
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.income_replacement_limit_per_week_cad,
    getRawRatedValue: (c) =>
      c.increased_accident_benefits_income_replacement?.limit_per_week_cad,
    showInCarrierCells: (coverages) =>
      coverages.accident_benefits?.income_replacement_enabled,
    isRequested: (coverages) =>
      coverages.accident_benefits?.income_replacement_enabled,
  },
  {
    label: 'Catastrophic Impairment',
    getRequestedValue: (coverages, _, getLabel) => {
      if (!coverages.accident_benefits?.catastrophic_impairment_enabled)
        return 'Declined';
      return formatCoverageLabel(
        coverages.accident_benefits?.catastrophic_impairment_limit_cad,
        getLabel,
        'catastrophic_impairment_limit_cad',
        'Base',
      );
    },
    getRatedValue: (c, getLabel) =>
      formatAccidentBenefitLabel(
        c.increased_accident_benefits_medical_rehabilitation_and_attendant_care
          ?.catastrophic_impairment?.limit_cad,
        getLabel,
        'catastrophic_impairment_limit_cad',
      ),
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.catastrophic_impairment_limit_cad,
    getRawRatedValue: (c) =>
      c.increased_accident_benefits_medical_rehabilitation_and_attendant_care
        ?.catastrophic_impairment?.limit_cad,
    showInCarrierCells: (coverages) =>
      coverages.accident_benefits?.catastrophic_impairment_enabled,
    isRequested: (coverages) =>
      coverages.accident_benefits?.catastrophic_impairment_enabled,
  },
  {
    label: 'Non-Catastrophic Impairment',
    getRequestedValue: (coverages, _, getLabel) => {
      if (!coverages.accident_benefits?.non_catastrophic_impairment_enabled)
        return 'Declined';
      return formatCoverageLabel(
        coverages.accident_benefits?.non_catastrophic_impairment_limit_cad,
        getLabel,
        'non_catastrophic_impairment_limit_cad',
        'Base',
      );
    },
    getRatedValue: (c, getLabel) =>
      formatAccidentBenefitLabel(
        c.increased_accident_benefits_medical_rehabilitation_and_attendant_care
          ?.non_catastrophic_impairment?.limit_cad,
        getLabel,
        'non_catastrophic_impairment_limit_cad',
      ),
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.non_catastrophic_impairment_limit_cad,
    getRawRatedValue: (c) =>
      c.increased_accident_benefits_medical_rehabilitation_and_attendant_care
        ?.non_catastrophic_impairment?.limit_cad,
    showInCarrierCells: (coverages) =>
      coverages.accident_benefits?.non_catastrophic_impairment_enabled,
    isRequested: (coverages) =>
      coverages.accident_benefits?.non_catastrophic_impairment_enabled,
  },
  {
    label: 'Caregiver Benefit',
    getRequestedValue: (coverages, _, getLabel) => {
      if (!coverages.accident_benefits?.caregiver_enabled) return 'Declined';
      const options = formatCoverageOptions(
        coverages.accident_benefits?.caregiver_coverage_options,
        getLabel,
        'caregiver_coverage_options',
      );
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.caregiver_limit_cad,
      );
      if (options) return `${options} (${limit || 'Base'})`;
      if (limit) return limit;
      return 'Base';
    },
    getRatedValue: (c) =>
      c.increased_accident_benefits_caregiver?.coverage ||
      c.opcf47r_optional_accident_benefits_coverage_and_priority_of_payment
        ?.optional_accident_benefits_coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.caregiver_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.increased_accident_benefits_caregiver?.coverage ||
        c.opcf47r_optional_accident_benefits_coverage_and_priority_of_payment
          ?.optional_accident_benefits_coverage
      ),
    isRequested: (coverages) => coverages.accident_benefits?.caregiver_enabled,
  },
  {
    label: 'Housekeeping & Home Maintenance',
    getRequestedValue: (coverages, _, getLabel) => {
      if (!coverages.accident_benefits?.housekeeping_home_maintenance_enabled)
        return 'Declined';
      const options = formatCoverageOptions(
        coverages.accident_benefits
          ?.housekeeping_home_maintenance_coverage_options,
        getLabel,
        'housekeeping_home_maintenance_coverage_options',
      );
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.housekeeping_home_maintenance_limit_cad,
      );
      if (options) return `${options} (${limit || 'Base'})`;
      if (limit) return limit;
      return 'Base';
    },
    getRatedValue: (c) =>
      c.increased_accident_benefits_housekeeping_and_home_maintenance
        ?.coverage ||
      c.opcf47r_optional_accident_benefits_coverage_and_priority_of_payment
        ?.optional_accident_benefits_coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.housekeeping_home_maintenance_enabled,
    getRawRatedValue: (c) =>
      !!(
        c.increased_accident_benefits_housekeeping_and_home_maintenance
          ?.coverage ||
        c.opcf47r_optional_accident_benefits_coverage_and_priority_of_payment
          ?.optional_accident_benefits_coverage
      ),
    isRequested: (coverages) =>
      coverages.accident_benefits?.housekeeping_home_maintenance_enabled,
  },
  {
    label: 'Death Benefit',
    getRequestedValue: (coverages) => {
      if (!coverages.accident_benefits?.death_benefit_enabled)
        return 'Declined';
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.death_benefit_limit_cad,
      );
      return limit || 'Base';
    },
    getRatedValue: (c) => c.increased_accident_benefits_death?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.death_benefit_enabled,
    getRawRatedValue: (c) => !!c.increased_accident_benefits_death?.coverage,
    isRequested: (coverages) =>
      coverages.accident_benefits?.death_benefit_enabled,
  },
  {
    label: 'Funeral Benefit',
    getRequestedValue: (coverages) => {
      if (!coverages.accident_benefits?.funeral_benefit_enabled)
        return 'Declined';
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.funeral_benefit_limit_cad,
      );
      return limit || 'Base';
    },
    getRatedValue: (c) => c.increased_accident_benefits_funeral?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.funeral_benefit_enabled,
    getRawRatedValue: (c) => !!c.increased_accident_benefits_funeral?.coverage,
    isRequested: (coverages) =>
      coverages.accident_benefits?.funeral_benefit_enabled,
  },
  {
    label: 'Dependant Care',
    getRequestedValue: (coverages) => {
      if (!coverages.accident_benefits?.dependant_care_enabled)
        return 'Declined';
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.dependant_care_limit_cad,
      );
      return limit || 'Base';
    },
    getRatedValue: (c) =>
      c.increased_accident_benefits_dependant_care?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.dependant_care_enabled,
    getRawRatedValue: (c) =>
      !!c.increased_accident_benefits_dependant_care?.coverage,
    isRequested: (coverages) =>
      coverages.accident_benefits?.dependant_care_enabled,
  },
  {
    label: 'Non-Earner Benefit',
    getRequestedValue: (coverages) => {
      if (!coverages.accident_benefits?.non_earner_enabled) return 'Declined';
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.non_earner_limit_cad,
      );
      return limit || 'Base';
    },
    getRatedValue: (c) => c.increased_accident_benefits_non_earner?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.non_earner_enabled,
    getRawRatedValue: (c) =>
      !!c.increased_accident_benefits_non_earner?.coverage,
    isRequested: (coverages) => coverages.accident_benefits?.non_earner_enabled,
  },
  {
    label: 'Lost Educational Expenses',
    getRequestedValue: (coverages) => {
      if (!coverages.accident_benefits?.lost_educational_expenses_enabled)
        return 'Declined';
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.lost_educational_expenses_limit_cad,
      );
      return limit || 'Base';
    },
    getRatedValue: (c) =>
      c.increased_accident_benefits_lost_educational_expenses?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.lost_educational_expenses_enabled,
    getRawRatedValue: (c) =>
      !!c.increased_accident_benefits_lost_educational_expenses?.coverage,
    isRequested: (coverages) =>
      coverages.accident_benefits?.lost_educational_expenses_enabled,
  },
  {
    label: 'Expenses of Visitors',
    getRequestedValue: (coverages) => {
      if (!coverages.accident_benefits?.expenses_of_visitors_enabled)
        return 'Declined';
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.expenses_of_visitors_limit_cad,
      );
      return limit || 'Base';
    },
    getRatedValue: (c) =>
      c.increased_accident_benefits_expenses_of_visitors?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.expenses_of_visitors_enabled,
    getRawRatedValue: (c) =>
      !!c.increased_accident_benefits_expenses_of_visitors?.coverage,
    isRequested: (coverages) =>
      coverages.accident_benefits?.expenses_of_visitors_enabled,
  },
  {
    label: 'Damage to Personal Items',
    getRequestedValue: (coverages) => {
      if (!coverages.accident_benefits?.damage_to_personal_items_enabled)
        return 'Declined';
      const limit = formatOptionalLimit(
        coverages.accident_benefits?.damage_to_personal_items_limit_cad,
      );
      return limit || 'Base';
    },
    getRatedValue: (c) =>
      c.increased_accident_benefits_damage_to_personal_items?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.damage_to_personal_items_enabled,
    getRawRatedValue: (c) =>
      !!c.increased_accident_benefits_damage_to_personal_items?.coverage,
    isRequested: (coverages) =>
      coverages.accident_benefits?.damage_to_personal_items_enabled,
  },
  {
    label: 'Indexation Benefit',
    getRequestedValue: (coverages) =>
      coverages.accident_benefits?.indexation_benefit_enabled
        ? 'Included'
        : 'Declined',
    getRatedValue: (c) =>
      c.increased_accident_benefits_indexation_benefit?.coverage,
    getRawRequestedValue: (coverages) =>
      coverages.accident_benefits?.indexation_benefit_enabled,
    getRawRatedValue: (c) =>
      !!c.increased_accident_benefits_indexation_benefit?.coverage,
    isRequested: (coverages) =>
      coverages.accident_benefits?.indexation_benefit_enabled,
  },
];

export const AUTO_COVERAGE_SECTIONS: AutoCoverageSectionConfig[] = [
  {
    key: 'physicalDamage',
    label: 'Physical Damage Coverage',
    rows: PHYSICAL_DAMAGE_ROWS,
  },
  {
    key: 'endorsements',
    label: 'Endorsements',
    rows: ENDORSEMENT_ROWS,
  },
  {
    key: 'discounts',
    label: 'Discounts',
    rows: DISCOUNT_ROWS,
  },
  {
    key: 'standardAB',
    label: 'Standard Accident Benefits',
    rows: STANDARD_AB_ROWS,
  },
  {
    key: 'optionalAB',
    label: 'Optional Accident Benefits',
    rows: OPTIONAL_AB_ROWS,
  },
];

export const getAllAutoCoverageRows = (): AutoCoverageRowConfig[] => {
  return [
    LIABILITY_ROW,
    ...PHYSICAL_DAMAGE_ROWS,
    ...ENDORSEMENT_ROWS,
    ...DISCOUNT_ROWS,
    ...STANDARD_AB_ROWS,
    ...OPTIONAL_AB_ROWS,
  ];
};
